import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  BarChart3,
  Calendar,
  FileSpreadsheet,
  Download,
  Filter,
  CheckCircle2,
  Users,
  Clock,
  Sparkles
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function ReportsPage({ businessInfo }) {
  const [bookings, setBookings] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("all");

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const res = await axios.get(`${API}/bookings`);
        setBookings(res.data || []);
      } catch (err) {
        console.error("Error fetching bookings:", err);
      }
    };
    fetchBookings();
  }, []);

  const daySlotCount = bookings.filter((b) => b.slot_type === "Day" || b.slot_type === "day").length;
  const nightSlotCount = bookings.filter((b) => b.slot_type === "Night" || b.slot_type === "night").length;
  const fullDayCount = bookings.filter((b) => b.slot_type === "Full Day" || b.slot_type === "full_day" || b.is_multi_day).length;

  const handleExportCsv = () => {
    if (bookings.length === 0) {
      alert("No data available to export.");
      return;
    }

    const headers = "Booking ID,Date,Slot,Customer Name,Phone,Total Amount,Advance Paid,Balance Due\n";
    const rows = bookings.map(b => {
      const total = b.total_amount || b.financial_details?.total_amount || b.rent_amount || 0;
      const adv = b.advance_paid || b.financial_details?.advance_paid || b.advance_amount || 0;
      const bal = b.balance_due !== undefined ? b.balance_due : (b.financial_details?.balance_due !== undefined ? b.financial_details.balance_due : (total - adv));
      const client = b.client_name || b.customer_name || 'Guest';
      const phone = b.client_phone || b.customer_phone || '';
      const dateVal = b.start_date || b.date || '';
      const slotVal = b.slot_type || 'Custom';
      return `"${b.id || b._id}","${dateVal}","${slotVal}","${client}","${phone}","${total}","${adv}","${bal}"`;
    }).join("\n");

    const blob = new Blob([headers + rows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `BookMyEvents_Report_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl mx-auto" data-testid="reports-page">
      {/* Top Header with Back Navigation */}
      <div className="flex items-center justify-between">
        <Link
          to="/calendar"
          data-testid="reports-back-btn"
          className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-pink-900 hover:text-pink-950 bg-white px-4 py-2 rounded-xl border border-pink-200 shadow-sm transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Calendar</span>
        </Link>

        <span className="text-xs font-semibold text-gray-500">
          Booking & Occupancy Reports
        </span>
      </div>

      {/* Main Container Card */}
      <div className="bg-white rounded-3xl border border-pink-100 shadow-xl overflow-hidden">
        {/* Banner */}
        <div className="bg-gradient-to-r from-pink-900 via-pink-800 to-rose-800 p-6 sm:p-8 text-white">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 text-xs font-semibold mb-2">
            <BarChart3 className="w-3.5 h-3.5 text-pink-200" />
            <span>Reports & Analytics</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight">
            Hall Occupancy & Event Reports
          </h1>
          <p className="text-pink-100 text-xs sm:text-sm mt-1 max-w-xl">
            Detailed breakdown of slot occupancies, day vs night distribution, and downloadable CSV statements for accounts.
          </p>
        </div>

        {/* Content Body */}
        <div className="p-6 sm:p-8 space-y-6 bg-gray-50/30">
          
          {/* Action Row */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-white p-4 rounded-2xl border border-pink-100 shadow-sm">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-pink-700" />
              <span className="text-xs font-bold text-gray-800">Filter Range:</span>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="text-xs font-semibold bg-pink-50 border border-pink-200 text-pink-950 rounded-xl px-3 py-1.5 focus:outline-none"
              >
                <option value="all">All Available Records</option>
                <option value="this-month">This Month</option>
                <option value="last-quarter">Last Quarter</option>
                <option value="this-year">Financial Year 2024-25</option>
              </select>
            </div>

            <button
              data-testid="export-csv-btn"
              onClick={handleExportCsv}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-pink-900 hover:bg-pink-950 text-white text-xs font-bold shadow-sm transition-all"
            >
              <Download className="w-4 h-4" />
              <span>Export CSV Statement</span>
            </button>
          </div>

          {/* Slot Breakdown Statistics */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            
            <div className="bg-white p-5 rounded-2xl border border-pink-100 shadow-sm text-center">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block">Day Functions</span>
              <div className="text-3xl font-black text-pink-950 mt-1" data-testid="day-slot-count">
                {daySlotCount}
              </div>
              <span className="text-[11px] text-gray-400 mt-1 block">Ends before 05:00 PM</span>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-pink-100 shadow-sm text-center">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block">Night Functions</span>
              <div className="text-3xl font-black text-pink-950 mt-1" data-testid="night-slot-count">
                {nightSlotCount}
              </div>
              <span className="text-[11px] text-gray-400 mt-1 block">05:00 PM to 11:59 PM</span>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-pink-100 shadow-sm text-center">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block">Full Day / Multi-Day</span>
              <div className="text-3xl font-black text-pink-950 mt-1" data-testid="full-day-count">
                {fullDayCount}
              </div>
              <span className="text-[11px] text-gray-400 mt-1 block">Grand Wedding Celebrations</span>
            </div>

          </div>

          {/* Summary Box */}
          <div className="bg-white p-6 rounded-2xl border border-pink-100 shadow-sm space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-pink-950 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>Occupancy Insights</span>
            </h3>
            <p className="text-xs text-gray-600 leading-relaxed">
              Your convention hall has registered <strong>{bookings.length}</strong> total confirmed bookings.
              Night function slots represent the highest demand period. Make sure default slot rates in 
              <Link to="/settings" className="text-pink-800 font-bold ml-1 underline">Business Settings</Link> are kept up to date.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
